import { useState } from "react";
import { BedDouble, Car, Compass, RefreshCcw, Sparkles, Trash2, UtensilsCrossed } from "lucide-react";
import { GoldImage } from "../GoldImage";
import { Stars, absPhoto, isActivity, metaPick, priceLabel, svcName } from "./utils";

/* Card servizio reale selezionato: foto, dati veri dal catalogo, azioni reali. */
const ServiceCard = ({ item, tc, lang, onDetail, onReplace, onRemove }) => {
    const m = item.meta || {};
    const metaLine = item.kind === "stay"
        ? (m.address || "")
        : item.kind === "transfer"
            ? `${metaPick(m.from, lang)} → ${metaPick(m.to, lang)}`
            : item.kind === "restaurant"
                ? [metaPick(m.type_label, lang), m.locality].filter(Boolean).join(" · ")
                : [m.locality, m.region || m.destination].filter(Boolean).join(" · ");
    return (
        <article className="group flex w-64 shrink-0 flex-col overflow-hidden rounded-xl border border-amber-500/20 bg-[#101012] transition-all hover:border-amber-400/45 hover:shadow-[0_8px_30px_rgba(212,175,55,0.12)] sm:w-full" data-testid={`service-card-${item.ref_id}`}>
            <button type="button" onClick={() => onDetail(item)} className="relative block h-36 w-full text-left" data-testid={`svc-open-${item.ref_id}`}>
                <GoldImage src={absPhoto(item.photo)} alt={svcName(item, lang)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-2.5 top-2.5 rounded-full border border-amber-400/50 bg-black/65 px-2.5 py-1 text-[9px] uppercase tracking-[0.12em] text-amber-200 backdrop-blur-sm">
                    {item.added_by === "ai" ? tc.selectedForYou : tc.addedByYou}
                </span>
            </button>
            <div className="flex flex-1 flex-col p-3.5">
                <h4 className="truncate font-display text-sm text-amber-50">{svcName(item, lang)}</h4>
                {metaLine && <p className="mt-0.5 truncate text-[10px] text-stone-500">{metaLine}</p>}
                <div className="mt-1.5"><Stars rating={m.rating} reviews={m.reviews} /></div>
                <div className="mt-auto flex items-center justify-between pt-3">
                    <span className={`text-[11px] font-semibold ${item.price != null || m.price_level ? "text-amber-300" : "text-stone-500"}`} data-testid={`svc-price-${item.ref_id}`}>
                        {priceLabel(item, tc)}
                    </span>
                    <span className="flex gap-1">
                        {item.kind !== "package" && (
                            <button onClick={() => onReplace(item)} data-testid={`svc-replace-${item.ref_id}`} aria-label={tc.replace} title={tc.replace}
                                className="flex h-7 w-7 items-center justify-center rounded-full border border-amber-500/30 text-amber-200 transition-colors hover:bg-amber-400/10">
                                <RefreshCcw size={11} />
                            </button>
                        )}
                        <button onClick={() => onRemove(item)} data-testid={`svc-remove-${item.ref_id}`} aria-label={tc.remove} title={tc.remove}
                            className="flex h-7 w-7 items-center justify-center rounded-full border border-red-400/30 text-red-300/90 transition-colors hover:bg-red-400/10">
                            <Trash2 size={11} />
                        </button>
                    </span>
                </div>
                <button onClick={() => onDetail(item)} data-testid={`svc-details-${item.ref_id}`}
                    className="mt-2.5 w-full rounded-full border border-amber-500/35 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-200 transition-colors hover:bg-amber-400/10">
                    {tc.seeDetails}
                </button>
            </div>
        </article>
    );
};

/* Sezioni cliccabili dei servizi selezionati: Alloggi / Esperienze / Transfer /
 * Ristoranti / Attività. Conteggi SOLO da dati reali dello stato viaggio. */
export const ServiceTabs = ({ trip, activeTab, setActiveTab, onDetail, onReplace, onRemove, onAskRestaurants, tc, lang, sectionRef }) => {
    const [showAll, setShowAll] = useState(false);
    const stays = trip?.stays || [];
    const expsAll = trip?.experiences || [];
    const exps = expsAll.filter((x) => !isActivity(x));
    const acts = expsAll.filter(isActivity);
    const transfers = trip?.transfers || [];
    const restaurants = trip?.restaurants || [];
    const tabs = [
        { id: "stays", label: tc.tabStays, icon: BedDouble, count: stays.length },
        { id: "experiences", label: tc.tabExperiences, icon: Compass, count: exps.length },
        { id: "transfers", label: tc.tabTransfers, icon: Car, count: transfers.length },
        { id: "restaurants", label: tc.tabRestaurants, icon: UtensilsCrossed, count: restaurants.length },
        { id: "activities", label: tc.tabActivities, icon: Sparkles, count: acts.length },
    ];
    const items = activeTab === "stays" ? stays : activeTab === "experiences" ? exps : activeTab === "transfers" ? transfers : activeTab === "restaurants" ? restaurants : activeTab === "activities" ? acts : null;
    const visible = items ? (showAll ? items : items.slice(0, 4)) : null;

    return (
        <section ref={sectionRef} className="rounded-2xl border border-amber-500/25 bg-[#0b0b0c]/90 p-5 sm:p-7" data-testid="service-tabs">
            <div className="flex gap-2 overflow-x-auto pb-1" role="tablist">
                {tabs.map((tb) => (
                    <button key={tb.id} role="tab" aria-selected={activeTab === tb.id} onClick={() => { setActiveTab(tb.id); setShowAll(false); }}
                        data-testid={`stab-${tb.id}`}
                        className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] transition-all ${activeTab === tb.id
                            ? "border-amber-300 bg-amber-400/15 text-amber-200 shadow-[0_0_16px_rgba(212,175,55,0.15)]"
                            : "border-amber-500/20 text-stone-400 hover:border-amber-400/40 hover:text-amber-100"}`}>
                        <tb.icon size={13} className={activeTab === tb.id ? "text-amber-300" : "text-amber-500/60"} />
                        {tb.label}
                        {tb.count != null && (
                            <span className={`rounded-full px-1.5 py-0.5 font-mono-accent text-[10px] ${activeTab === tb.id ? "bg-amber-300 text-black" : "bg-white/5 text-stone-400"}`} data-testid={`stab-count-${tb.id}`}>
                                {tb.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            <div className="mt-5">
                {!items || items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-amber-500/20 p-8 text-center" data-testid="services-empty">
                        <p className="mx-auto max-w-md text-xs italic leading-relaxed text-stone-500">
                            {activeTab === "restaurants" ? tc.restEmpty : tc.emptyServices}
                        </p>
                        {activeTab === "restaurants" && (
                            <button onClick={onAskRestaurants} data-testid="ask-restaurants-btn"
                                className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-200 transition-colors hover:bg-amber-400/10">
                                <Sparkles size={12} /> {tc.askRestaurants}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="flex gap-4 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible xl:grid-cols-4" data-testid="service-list">
                            {visible.map((item) => (
                                <ServiceCard key={`${item.kind}-${item.ref_id}`} item={item} tc={tc} lang={lang}
                                    onDetail={onDetail} onReplace={onReplace} onRemove={onRemove} />
                            ))}
                        </div>
                        {items.length > 4 && (
                            <button onClick={() => setShowAll(!showAll)} data-testid="services-see-all"
                                className="mt-4 text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-300 transition-colors hover:text-amber-200">
                                {showAll ? tc.seeLess : `${tc.seeAll} (${items.length})`} →
                            </button>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};
